import { useEffect, useRef, useState } from 'react';
import { REALTIME_CONNECTION_STATUS } from '../../../services/realtime/constants';
import { useReferenceVideoStatus } from '../../../zustand/reference-video-status-store';
import {
  deriveCameraState,
  deriveExerciseState,
  deriveVideoState,
  deriveVoiceState,
  EXERCISE_STATE,
  initialSocketTrack,
  nextSocketTrack,
  RESTORED_VISIBLE_MS,
  summarizeSeverity,
} from '../connection-status';

// Re-render cadence while something time-based is pending (a drop waiting out
// its delay, a restored message counting down). Stops when nothing is.
const TICK_MS = 500;

/**
 * Measures how long each existing signal has been in trouble and hands the
 * durations to the pure mappers in ../connection-status. Observation only: it
 * reads the socket's readyState, the realtime store's status, the reference
 * video's events and the camera flags, and never calls back into any of them —
 * reconnects, retries and timers all stay exactly where they were.
 */
export default function useConnectionStatus({
  socketStatus,
  voiceEnabled,
  voiceStatus,
  hasVideoSource,
  usesCamera,
  isCameraReacquiring,
  isCameraMissing,
  // NETWORK_STATE from use-network-quality, or null when it is not running.
  networkState = null,
}) {
  const isSocketOpen = socketStatus === 'Open';
  const [now, setNow] = useState(() => Date.now());
  const [socketTrack, setSocketTrack] = useState(() => initialSocketTrack(Date.now()));
  // Whether the patient actually saw an interruption during the current drop —
  // "restored" only confirms something they were told about.
  const issueShownRef = useRef(false);

  useEffect(() => {
    const issueShown = issueShownRef.current;

    if (isSocketOpen) {
      issueShownRef.current = false;
    }

    setSocketTrack(prev =>
      nextSocketTrack(prev, { isOpen: isSocketOpen, at: Date.now(), issueShown })
    );
  }, [isSocketOpen]);

  // First error since the voice call was last open; retries keep it running
  // (they flip the status through CONNECTING), an open clears it.
  const [voiceTroubleSince, setVoiceTroubleSince] = useState(null);

  useEffect(() => {
    if (voiceStatus === REALTIME_CONNECTION_STATUS.OPEN) {
      setVoiceTroubleSince(null);
    } else if (voiceStatus === REALTIME_CONNECTION_STATUS.ERROR) {
      setVoiceTroubleSince(prev => prev ?? Date.now());
    }
  }, [voiceStatus]);

  const videoWaitingSince = useReferenceVideoStatus(state => state.waitingSince);
  const videoHasError = useReferenceVideoStatus(state => state.hasError);

  const since = start => (start === null ? null : Math.max(0, now - start));

  const exercise = deriveExerciseState({
    isOpen: isSocketOpen,
    hasEverOpened: socketTrack.hasEverOpened,
    downForMs: since(socketTrack.downSince),
    restoredForMs: since(socketTrack.restoredAt),
  });

  const voice = deriveVoiceState({
    enabled: voiceEnabled,
    isOpen: voiceStatus === REALTIME_CONNECTION_STATUS.OPEN,
    isIdle: voiceStatus === REALTIME_CONNECTION_STATUS.IDLE,
    troubleForMs: since(voiceTroubleSince),
  });

  const video = deriveVideoState({
    hasSource: hasVideoSource,
    hasError: videoHasError,
    waitingForMs: since(videoWaitingSince),
  });

  const camera = deriveCameraState({
    usesCamera,
    isReacquiring: isCameraReacquiring,
    isMissing: isCameraMissing,
  });

  useEffect(() => {
    if (exercise === EXERCISE_STATE.reconnecting || exercise === EXERCISE_STATE.failed) {
      issueShownRef.current = true;
    }
  }, [exercise]);

  const isRestoredCountingDown =
    socketTrack.restoredAt !== null && now - socketTrack.restoredAt < RESTORED_VISIBLE_MS;
  const needsTick =
    !isSocketOpen ||
    isRestoredCountingDown ||
    (voiceEnabled && voiceTroubleSince !== null) ||
    videoWaitingSince !== null;

  useEffect(() => {
    if (!needsTick) {
      return undefined;
    }

    setNow(Date.now());
    const intervalId = window.setInterval(() => setNow(Date.now()), TICK_MS);

    return () => window.clearInterval(intervalId);
  }, [needsTick]);

  const states = { network: networkState, exercise, voice, video, camera };

  return { ...states, severity: summarizeSeverity(states) };
}
